import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db, auth } from '../firebase';
import { doc, updateDoc, collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Chat Widget panel (client profile card)
//
// Two tabs:
//   Settings      — the ONLY place the AI chat widget's grounding data is
//                   maintained. The chat function (chatWidgetHandler) reads
//                   exactly three things: these fields, the client's brandVoice
//                   notes, and the uploaded Service Pages document — nothing
//                   else. Monthly reports, Clarity, Insights, and the Brand Kit
//                   document are structurally out of reach.
//   Conversations — every stored transcript for the range, with the outcome
//                   analysis the hourly chatAnalysisScheduler writes onto each
//                   one, an aggregated "questions the bot could not answer"
//                   list that feeds straight into Approved Answers, and the
//                   bot's unsupported claims so staff see a bot going wrong
//                   before the client does.
//
// Reads clients/{id}/chat_logs directly (covered by the recursive isStaff rule
// on /clients). Writes go through the staff-authed manual-trigger functions.
// ---------------------------------------------------------------------------

const FN_BASE = 'https://us-central1-core6-marketing.cloudfunctions.net';

const FACT_FIELDS = [
  { key: 'phone',       label: 'Phone',            placeholder: '(831) 555-0100' },
  { key: 'email',       label: 'Public Email',     placeholder: 'info@example.com' },
  { key: 'address',     label: 'Address',          placeholder: '123 Main St, Salinas, CA 93901' },
  { key: 'hours',       label: 'Hours',            placeholder: 'Mon–Fri 8am–5pm, Sat 9am–1pm, closed Sunday' },
  { key: 'serviceArea', label: 'Service Area',     placeholder: 'Monterey, Salinas, Seaside, Marina, Carmel' },
  { key: 'categories',  label: 'Business Type',    placeholder: 'Residential plumbing, drain cleaning, water heaters' },
  { key: 'bookingUrl',  label: 'Booking Link',     placeholder: 'https://example.com/book' },
];

const RANGES = [
  { days: 7,  label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const emptyFacts = () => FACT_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), { notes: '' });

function newSiteKey() {
  const rand = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())).replace(/-/g, '');
  return `c6chat_${rand}`;
}

async function callFn(name, body) {
  const idToken = await auth.currentUser?.getIdToken();
  const res = await fetch(`${FN_BASE}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

function fmtWhen(ts) {
  const d = tsToDate(ts);
  return d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
}

function normalizeQuestion(q) {
  return String(q || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}

// Outcome as TEXT with a filled chip. Lightness carries the weight, never hue
// alone: the two states that need action are dark-on-light, the rest muted.
function outcomeOf(c) {
  const a = c.analysis;
  if (c.handed_off) return { label: 'Handed off', tone: 'action' };
  if (!a) return { label: 'Awaiting review', tone: 'muted' };
  if (a.error) return { label: 'Review failed', tone: 'muted' };
  if (a.needs_attention) return { label: 'Needs attention', tone: 'action' };
  switch (a.outcome) {
    case 'answered':           return { label: 'Answered', tone: 'ok' };
    case 'partially_answered': return { label: 'Partly answered', tone: 'warn' };
    case 'unanswered':         return { label: 'Could not answer', tone: 'warn' };
    case 'off_topic':          return { label: 'Off topic', tone: 'muted' };
    case 'empty':              return { label: 'No message', tone: 'muted' };
    default:                   return { label: 'Reviewed', tone: 'muted' };
  }
}

function Chip({ label, tone }) {
  const styles = {
    action: { background: '#f8fafc', color: '#0f172a', border: '1px solid #0f172a' },
    warn:   { background: '#1f2937', color: '#f8fafc', border: '1px solid #4b5563' },
    ok:     { background: 'transparent', color: 'var(--text-primary)', border: '1px solid var(--border)' },
    muted:  { background: 'transparent', color: 'var(--text-muted)', border: '1px dashed var(--border)' },
  }[tone] || {};
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap', ...styles }}>
      {label}
    </span>
  );
}

function Stat({ n, label }) {
  return (
    <div style={{ flex: 1, minWidth: 110, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{n}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}

const sectionTitle = { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-muted)' };

export default function ChatWidgetPanel({ clientId, client }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('settings');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [keyVisible, setKeyVisible] = useState(false);
  const [notice, setNotice] = useState('');

  // Conversations tab
  const [rangeDays, setRangeDays] = useState(30);
  const [convos, setConvos] = useState(null);
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(''); // 'analyze' | 'digest' | 'rollup'
  const [actionMsg, setActionMsg] = useState(null);

  // Seed once from the client doc, then let the form own its state so typing
  // isn't clobbered by the profile's live snapshot listener.
  useEffect(() => {
    if (!client || form !== null) return;
    const k = client.chatKnowledge || {};
    setForm({
      chatEnabled: client.chatEnabled ?? false,
      chatDigestEnabled: client.chatDigestEnabled ?? false,
      chatSiteKey: client.chatSiteKey || '',
      chatRepEmail: client.chatRepEmail || '',
      facts: { ...emptyFacts(), ...(k.businessFacts || {}) },
      faqs: Array.isArray(k.faqs) ? k.faqs.map(f => ({ question: f.question || '', answer: f.answer || '' })) : [],
    });
  }, [client, form]);

  const loadConvos = useCallback(async () => {
    setLoadingConvos(true);
    try {
      const since = Timestamp.fromDate(new Date(Date.now() - rangeDays * 86400000));
      const q = query(
        collection(db, 'clients', clientId, 'chat_logs'),
        where('started_at', '>=', since),
        orderBy('started_at', 'desc'),
        limit(300),
      );
      const snap = await getDocs(q);
      setConvos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setActionMsg({ ok: false, text: 'Could not load conversations: ' + e.message });
      setConvos([]);
    } finally {
      setLoadingConvos(false);
    }
  }, [clientId, rangeDays]);

  useEffect(() => {
    if (open && tab === 'conversations') loadConvos();
  }, [open, tab, rangeDays, loadConvos]);

  // ---- derived views over the loaded conversations ----
  const derived = useMemo(() => {
    const list = convos || [];
    const real = list.filter(c => !(c.analysis && (c.analysis.outcome === 'empty' || c.analysis.outcome === 'off_topic')));
    const agg = new Map();
    for (const c of real) {
      const qs = c.analysis && Array.isArray(c.analysis.unanswered_questions) ? c.analysis.unanswered_questions : [];
      const seen = new Set();
      for (const q of qs) {
        const key = normalizeQuestion(q);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        const cur = agg.get(key) || { key, question: q, count: 0, ids: [] };
        cur.count++; cur.ids.push(c.id);
        agg.set(key, cur);
      }
    }
    const claims = [];
    for (const c of list) {
      const cl = c.analysis && Array.isArray(c.analysis.unsupported_claims) ? c.analysis.unsupported_claims : [];
      cl.forEach(text => claims.push({ id: c.id, text, when: c.started_at }));
    }
    return {
      total: list.length,
      handedOff: list.filter(c => c.handed_off).length,
      attention: list.filter(c => !c.handed_off && c.analysis && c.analysis.needs_attention).length,
      couldNotAnswer: real.filter(c => c.analysis && c.analysis.outcome === 'unanswered').length,
      awaiting: list.filter(c => !c.analysis).length,
      unanswered: [...agg.values()].sort((a, b) => b.count - a.count),
      claims,
    };
  }, [convos]);

  if (!form) return null;

  const set = (patch) => { setForm(f => ({ ...f, ...patch })); setSaved(false); };
  const setFact = (key, value) => { setForm(f => ({ ...f, facts: { ...f.facts, [key]: value } })); setSaved(false); };
  const setFaq = (i, patch) => {
    setForm(f => ({ ...f, faqs: f.faqs.map((q, idx) => idx === i ? { ...q, ...patch } : q) }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const faqs = form.faqs
        .map(f => ({ question: (f.question || '').trim(), answer: (f.answer || '').trim() }))
        .filter(f => f.question && f.answer);
      await updateDoc(doc(db, 'clients', clientId), {
        chatEnabled: !!form.chatEnabled,
        chatDigestEnabled: !!form.chatDigestEnabled,
        chatSiteKey: form.chatSiteKey || '',
        chatRepEmail: (form.chatRepEmail || '').trim(),
        chatKnowledge: { businessFacts: form.facts, faqs },
      });
      setForm(f => ({ ...f, faqs }));
      setSaved(true);
      setNotice('');
    } catch (e) {
      setError('Could not save: ' + e.message);
    }
    setSaving(false);
  };

  // The closed loop: a question the bot could not answer becomes a blank
  // Approved Answer for staff to fill in, on the Settings tab, unsaved until
  // they write the answer and hit Save.
  const addAsApprovedAnswer = (question) => {
    const exists = form.faqs.some(f => normalizeQuestion(f.question) === normalizeQuestion(question));
    if (!exists) set({ faqs: [...form.faqs, { question, answer: '' }] });
    setNotice(exists
      ? 'That question is already in Approved Answers below.'
      : 'Added to Approved Answers at the bottom of Settings. Write the answer, then Save Chat Settings.');
    setTab('settings');
  };

  const runAction = async (kind, fn, okText) => {
    setBusy(kind);
    setActionMsg(null);
    try {
      const data = await fn();
      setActionMsg({ ok: true, text: okText(data) });
      if (kind === 'analyze') await loadConvos();
    } catch (e) {
      setActionMsg({ ok: false, text: e.message });
    } finally {
      setBusy('');
    }
  };

  const analyzeNow = () => runAction('analyze',
    () => callFn('chatAnalysisRun', { clientId, force: true }),
    d => d.skipped ? `Skipped: ${d.skipped}.` : `Reviewed ${d.analyzed || 0} conversation${d.analyzed === 1 ? '' : 's'}${d.failed ? `, ${d.failed} failed` : ''}.`);

  const sendTestDigest = () => runAction('digest',
    () => callFn('chatDigestSend', { clientId, to: auth.currentUser?.email, days: 7 }),
    d => d.skipped ? `Nothing sent: ${d.skipped}.` : `Sent to ${d.to} — ${d.counts.total} conversations, PDF ${Math.round(d.pdfBytes / 1024)} KB.`);

  const rollupNow = () => runAction('rollup',
    () => callFn('chatVocRollupRun', { clientId }),
    d => d.skipped ? `Skipped: ${d.skipped}.` : `Wrote the Chat Questions file to Content Data — ${d.conversations} conversations, ${d.themes} themes, ${d.gaps} content gaps.`);

  const enabled = !!form.chatEnabled;
  const faqCount = form.faqs.filter(f => (f.question || '').trim() && (f.answer || '').trim()).length;
  const tabBtn = (id, label) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`btn btn-sm ${tab === id ? 'btn-primary' : 'btn-secondary'}`}
      style={{ fontSize: 12 }}
    >
      {label}
    </button>
  );

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>AI Chat Widget</div>
          {/* Text label, not a colour dot — the state has to read without colour. */}
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 4,
            background: enabled ? '#0a1f12' : 'var(--bg-primary)',
            border: `1px solid ${enabled ? '#16a34a55' : 'var(--border)'}`,
            color: enabled ? '#4ade80' : 'var(--text-muted)',
          }}>
            {enabled ? 'Live' : 'Off'}
          </span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{open ? '▾ Hide' : '▸ Show'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
            {tabBtn('settings', 'Settings')}
            {tabBtn('conversations', 'Conversations')}
          </div>

          {/* ═══════════════════════ SETTINGS ═══════════════════════════ */}
          {tab === 'settings' && (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 18 }}>
                Everything the website chat bot knows about this client lives here, plus their brand voice notes
                and the uploaded Service Pages document. Monthly reports, Clarity, Insights, and the Brand Kit
                document are never sent to it — if the bot needs a fact, it has to be on this page.
              </div>

              {notice && (
                <div style={{ marginBottom: 14, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
                  {notice}
                </div>
              )}

              <div className="form-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={enabled} onChange={e => set({ chatEnabled: e.target.checked })} />
                  Chat widget enabled for this client
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Hard gate — with this off, the chat endpoint refuses every request for this client
                  regardless of what the WordPress plugin is set to.
                </div>
              </div>

              <div className="form-field">
                <label>Client ID (paste into the WordPress plugin)</label>
                <input type="text" readOnly value={clientId} style={{ fontFamily: 'monospace', fontSize: 12 }} />
              </div>

              <div className="form-field">
                <label>Site Key (paste into the WordPress plugin)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type={keyVisible ? 'text' : 'password'}
                    readOnly
                    value={form.chatSiteKey}
                    placeholder="No key generated yet"
                    style={{ fontFamily: 'monospace', fontSize: 12, flex: 1 }}
                  />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setKeyVisible(v => !v)}>
                    {keyVisible ? 'Hide' : 'Show'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      if (form.chatSiteKey && !window.confirm('Replace the existing site key? The plugin on this client\'s site will stop reading chat logs until the new key is pasted in.')) return;
                      set({ chatSiteKey: newSiteKey() });
                      setKeyVisible(true);
                    }}
                  >
                    {form.chatSiteKey ? 'Regenerate' : 'Generate'}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Server-to-server only — used by the plugin's admin screen to read this client's chat logs.
                  It is never exposed to site visitors. Save after generating.
                </div>
              </div>

              <div className="form-field">
                <label>Rep Notification Email</label>
                <input
                  type="email"
                  placeholder="rep@example.com"
                  value={form.chatRepEmail}
                  onChange={e => set({ chatRepEmail: e.target.value })}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Where handoff transcripts and the weekly digest are emailed. Stored here rather than taken from
                  the public request, so the chat endpoint can't be used to send mail to an arbitrary address.
                  Editing it in the WordPress plugin writes back to this field.
                </div>
              </div>

              {/* ── Weekly digest ─────────────────────────────────────── */}
              <div className="form-field" style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!form.chatDigestEnabled}
                    onChange={e => set({ chatDigestEnabled: e.target.checked })}
                    disabled={!form.chatRepEmail}
                  />
                  Email the rep a weekly chat digest (Mondays, 7am Pacific)
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
                  Counts, visitors handed to the team, conversations worth a follow-up, the questions the bot
                  could not answer, and every transcript from the week attached as a PDF. Skipped on weeks with
                  no conversations. Needs a rep email above. Off by default — send yourself a test first.
                  {client.chatDigestLastSentAt && <> Last sent {fmtWhen(client.chatDigestLastSentAt)}.</>}
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={sendTestDigest} disabled={busy !== ''}>
                    {busy === 'digest' ? 'Building…' : `Send me a test (last 7 days)`}
                  </button>
                  {actionMsg && tab === 'settings' && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: actionMsg.ok ? 'var(--text-primary)' : '#f87171' }}>{actionMsg.text}</span>
                  )}
                </div>
              </div>

              {/* ── Business facts ────────────────────────────────────── */}
              <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
                <div style={{ ...sectionTitle, marginBottom: 14 }}>Business Details</div>
                {FACT_FIELDS.map(f => (
                  <div className="form-field" key={f.key}>
                    <label>{f.label}</label>
                    <input type="text" placeholder={f.placeholder} value={form.facts[f.key] || ''} onChange={e => setFact(f.key, e.target.value)} />
                  </div>
                ))}
                <div className="form-field">
                  <label>Other Details</label>
                  <textarea
                    rows={3}
                    placeholder="Anything else a visitor might ask — payment methods, emergency availability, languages spoken, parking, licence numbers."
                    value={form.facts.notes || ''}
                    onChange={e => setFact('notes', e.target.value)}
                  />
                </div>
              </div>

              {/* ── FAQ ───────────────────────────────────────────────── */}
              <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={sectionTitle}>Approved Answers ({faqCount})</div>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => set({ faqs: [...form.faqs, { question: '', answer: '' }] })}>
                    + Add
                  </button>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
                  Questions real customers actually ask — from calls, forms, reviews, and the Conversations tab —
                  with the answer you want given. The bot prefers these over anything it infers from the service pages.
                </div>

                {form.faqs.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '14px 0' }}>
                    No approved answers yet. The bot will fall back to the service pages and business details above.
                  </div>
                )}

                {form.faqs.map((f, i) => (
                  <div key={i} style={{ border: `1px solid ${f.answer ? 'var(--border)' : '#0f172a'}`, borderRadius: 8, padding: 14, marginBottom: 10 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <input
                          type="text"
                          placeholder="Question — e.g. Do you offer free estimates?"
                          value={f.question}
                          onChange={e => setFaq(i, { question: e.target.value })}
                          style={{ width: '100%', marginBottom: 8 }}
                        />
                        <textarea
                          rows={2}
                          placeholder={f.question && !f.answer ? 'Needs an answer — the bot will not use this entry until one is written' : 'Answer the bot should give'}
                          value={f.answer}
                          onChange={e => setFaq(i, { answer: e.target.value })}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => set({ faqs: form.faqs.filter((_, idx) => idx !== i) })} title="Remove this answer">
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {error && <div style={{ marginTop: 14, fontSize: 12, color: '#f87171', fontWeight: 600 }}>{error}</div>}

              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Chat Settings'}
                </button>
                {saved && <span style={{ fontSize: 12, fontWeight: 600, color: '#4ade80' }}>✓ Saved</span>}
              </div>
            </>
          )}

          {/* ═══════════════════════ CONVERSATIONS ══════════════════════ */}
          {tab === 'conversations' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {RANGES.map(r => (
                    <button
                      key={r.days}
                      type="button"
                      className={`btn btn-sm ${rangeDays === r.days ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: 11 }}
                      onClick={() => setRangeDays(r.days)}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={analyzeNow} disabled={busy !== ''} title="Review every conversation that has not been reviewed yet, including ones that just ended">
                    {busy === 'analyze' ? 'Reviewing…' : `Review now${derived.awaiting ? ` (${derived.awaiting} waiting)` : ''}`}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={rollupNow} disabled={busy !== ''} title="Write the last 90 days of visitor questions into the Chat Questions slot in Content Data">
                    {busy === 'rollup' ? 'Rolling up…' : 'Roll up to Content Data'}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={loadConvos} disabled={loadingConvos}>
                    {loadingConvos ? '…' : 'Refresh'}
                  </button>
                </div>
              </div>

              {actionMsg && (
                <div style={{ marginBottom: 14, fontSize: 12, fontWeight: 600, color: actionMsg.ok ? 'var(--text-primary)' : '#f87171' }}>{actionMsg.text}</div>
              )}

              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
                Each finished conversation is reviewed automatically about an hour after it ends, against the same
                business information the bot had. Reviews are anonymized; transcripts are not.
                {client.chatVocLastRolledUpAt && <> Last Content Data rollup {fmtWhen(client.chatVocLastRolledUpAt)}.</>}
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                <Stat n={derived.total} label="Conversations" />
                <Stat n={derived.handedOff} label="Handed off" />
                <Stat n={derived.attention} label="Needs attention" />
                <Stat n={derived.couldNotAnswer} label="Could not answer" />
                <Stat n={derived.awaiting} label="Awaiting review" />
              </div>

              {/* ── Questions the bot could not answer → Approved Answers ── */}
              {derived.unanswered.length > 0 && (
                <div style={{ marginBottom: 22, padding: 14, border: '1px solid #0f172a', borderRadius: 8 }}>
                  <div style={{ ...sectionTitle, marginBottom: 6 }}>Questions the bot could not answer ({derived.unanswered.length})</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
                    Each becomes a blank Approved Answer on the Settings tab. Write the answer, save, and the bot
                    knows it from the next conversation on.
                  </div>
                  {derived.unanswered.slice(0, 25).map(q => (
                    <div key={q.key} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0', borderTop: '1px solid var(--border)' }}>
                      <span style={{ minWidth: 28, fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>{q.count > 1 ? `${q.count}×` : ''}</span>
                      <span style={{ flex: 1, fontSize: 13 }}>{q.question}</span>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => addAsApprovedAnswer(q.question)}>
                        Add approved answer
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Claims the bot made that its information did not support ── */}
              {derived.claims.length > 0 && (
                <div style={{ marginBottom: 22, padding: 14, border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ ...sectionTitle, marginBottom: 6 }}>Statements to verify ({derived.claims.length})</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
                    Specific things the bot told a visitor that were not in its business details, approved answers,
                    or service pages. Either add the fact so it is grounded, or tell the bot not to say it.
                  </div>
                  {derived.claims.slice(0, 25).map((cl, i) => (
                    <div key={`${cl.id}-${i}`} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0', borderTop: '1px solid var(--border)' }}>
                      <span style={{ flex: 1, fontSize: 13 }}>{cl.text}</span>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setExpanded(cl.id)}>
                        {fmtWhen(cl.when)} · view
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* ── The list ────────────────────────────────────────────── */}
              {loadingConvos && convos === null && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
              {convos && convos.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '14px 0' }}>No conversations in the last {rangeDays} days.</div>}

              {convos && convos.map(c => {
                const a = c.analysis;
                const oc = outcomeOf(c);
                const first = (c.turns || []).find(t => t.role === 'visitor');
                const isOpen = expanded === c.id;
                const v = c.handoff_contact || {};
                return (
                  <div key={c.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <div
                      onClick={() => setExpanded(isOpen ? null : c.id)}
                      style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', cursor: 'pointer' }}
                    >
                      <span style={{ minWidth: 120, fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtWhen(c.started_at)}</span>
                      <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a && a.summary ? a.summary : (first ? first.text : <span style={{ color: 'var(--text-muted)' }}>(no visitor message)</span>)}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{(c.turns || []).length} turns</span>
                      <Chip label={oc.label} tone={oc.tone} />
                    </div>

                    {isOpen && (
                      <div style={{ padding: '4px 0 16px 132px' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.6 }}>
                          {c.page_url && <div>Page: <a href={c.page_url} target="_blank" rel="noopener noreferrer">{c.page_url}</a></div>}
                          {c.handed_off && <div>Handed off to: {[v.name, v.email, v.phone].filter(Boolean).join(' · ') || '—'}{c.handoff_email_sent === false && ' (email failed)'}</div>}
                          {a && a.intent && <div>Intent: {a.intent.replace(/_/g, ' ')}{a.topics && a.topics.length ? ` · Topics: ${a.topics.join(', ')}` : ''}</div>}
                          {a && a.needs_attention && a.attention_reason && <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Attention: {a.attention_reason}</div>}
                          {a && a.unanswered_questions && a.unanswered_questions.length > 0 && <div>Could not answer: {a.unanswered_questions.join(' · ')}</div>}
                          {a && a.unsupported_claims && a.unsupported_claims.length > 0 && <div style={{ fontWeight: 600 }}>Unsupported: {a.unsupported_claims.join(' · ')}</div>}
                        </div>
                        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px' }}>
                          {(c.turns || []).map((t, i) => {
                            const isV = t.role === 'visitor';
                            return (
                              <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                                <span style={{ minWidth: 64, fontSize: 11, fontWeight: 700, color: isV ? 'var(--text-primary)' : 'var(--text-muted)' }}>{isV ? 'Visitor' : 'Assistant'}</span>
                                <span style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{t.text}</span>
                              </div>
                            );
                          })}
                          {(c.turns || []).length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No messages stored.</div>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
